<template>
  <BaseModal
    :heading="$t('wavy_onboarding.copy_wavy_data.heading', brandName())"
    small
    useForm
    @close="emit('close')"
    @submit="submit"
  >
    <FirstStep v-if="state === 'initial'" />
    <ProcessingStep
      v-else
      :state="state"
      :progress="progressPercentage"
    />
    <template
      #footer
      v-if="state !== 'loading'"
    >
      <BaseButton
        color="inverted"
        @click="emit('close')"
      >
        {{ state === 'initial' ? $t('global.actions.cancel') : $t('global.actions.close') }}
      </BaseButton>
      <BaseButton
        color="primary"
        submitForm
        :routerLink="state === 'success' ? { name: 'calendar' } : undefined"
        @click="state === 'initial' ? undefined : emit('close')"
        v-test="'wavy-onboarding-copy-data-submit'"
      >
        {{ ctaText }}
      </BaseButton>
    </template>
  </BaseModal>
</template>

<script
  setup
  lang="ts"
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { useI18n } from 'vue-i18n';
import { brandName } from '@/helpers/brand-name';
import FirstStep from './FirstStep.vue';
import ProcessingStep from './ProcessingStep.vue';

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();
const state = ref<'initial' | 'loading' | 'success' | 'error'>('initial');
const progressPercentage = ref(0);

const ctaText = computed(() => {
  switch (state.value) {
    case 'initial':
      return t('wavy_onboarding.copy_wavy_data.button_start_data_copy');
    case 'success':
      return t('wavy_onboarding.copy_wavy_data.button_go_to_calendar');
    case 'error':
      return t('wavy_onboarding.copy_wavy_data.button_contact_support');
  }
});

const { mutate: triggerMigration } = useMutation(gql`
  mutation triggerWavyMigration($input: TriggerWavyMigrationInput!) {
    triggerWavyMigration(input: $input) {
      requested
    }
  }
`);

const submit = () => {
  if (state.value === 'initial') {
    triggerMigration({
      input: {},
    });

    state.value = 'loading';

    const { mutate: refreshMigrationStatus } = useMutation(gql`
      mutation refreshWavyMigrationStatus($input: RefreshWavyMigrationStatusInput!) {
        refreshWavyMigrationStatus(input: $input) {
          lastPolledAt
          status
          errors {
            type
            message
          }
          progress
        }
      }
    `);

    const interval = setInterval(() => {
      refreshMigrationStatus({
        input: {},
      })
        .then(({ data }) => {
          if (data?.refreshWavyMigrationStatus?.status === 'COMPLETE') {
            clearInterval(interval);
            state.value = 'success';
            emit('success');
          } else if (
            data?.refreshWavyMigrationStatus?.status === 'IN_PROGRESS'
          ) {
            progressPercentage.value =
              data?.refreshWavyMigrationStatus?.progress || 0;
          }
        })
        .catch(() => {
          clearInterval(interval);
          state.value = 'error';
        });
    }, 3000);
  }
};
</script>
